import { useMemo, useState, type FC } from "react";
import type { DashboardViewProps } from "../../types/dashboardView";
import { AnalyticChartPanel } from "./analyticShared/AnalyticChartPanel";
import { AnalyticLegend } from "./analyticShared/AnalyticLegend";
import { remainingWeightSeries } from "./analyticShared/completionSeries";
import { useScopeChildren } from "./analyticShared/scopeChildren";
import { useChartBox } from "./analyticShared/useChartBox";
import { CHART_THEME_COLORS } from "../Common/Chart/chartTheme";
import { AreaChart } from "../Common/Chart/AreaChart";
import type { ChartResult, ChartSpec } from "../../types/chart";

const TICKS = 12;

export const FlowbandView: FC<DashboardViewProps> = ({
  elements,
  viewStartMs,
  viewEndMs,
}) => {
  const [currentMs] = useState(() => Date.now());
  const { parentRef, width, height } = useChartBox();
  const scope = useScopeChildren(elements);
  const ids = useMemo(
    () => new Map(elements.map((e) => [e.id, e])),
    [elements],
  );
  const raw = useMemo(
    () =>
      remainingWeightSeries(
        scope.children,
        ids,
        { startMs: viewStartMs, endMs: viewEndMs },
        TICKS,
      ),
    [scope.children, ids, viewStartMs, viewEndMs],
  );
  const order = useMemo(
    () =>
      raw
        .map((_, i) => i)
        .sort((a, b) => (raw[b][TICKS - 1] ?? 0) - (raw[a][TICKS - 1] ?? 0)),
    [raw],
  );
  const children = order.map((i) => scope.children[i]);
  const series = order.map((i) => raw[i]);
  const colors = useMemo(
    () =>
      children.map((child, i) =>
        child.ownColor && /^#[0-9a-fA-F]{6}$/.test(child.ownColor)
          ? child.ownColor
          : CHART_THEME_COLORS[i % CHART_THEME_COLORS.length],
      ),
    [children],
  );

  const caption = (
    <>
      Scope: <strong>{scope.parent?.title ?? "Board"}</strong> · showing{" "}
      <strong>{children.length}</strong> {scope.nextLevelName}s, coloured as{" "}
      <em>{scope.nextLevelName}s</em>.
    </>
  );
  const legend = children.length ? (
    <AnalyticLegend
      children={children}
      colors={colors}
      nextLevelName={scope.nextLevelName}
      onDrillInto={scope.drillInto}
      terminal={scope.nextLevelName === "checklist"}
    />
  ) : null;

  const maxTotal = useMemo(() => {
    const totals = Array.from({ length: TICKS }, (_, tick) =>
      series.reduce((sum, row) => sum + (row[tick] ?? 0), 0),
    );
    return Math.max(0, ...totals);
  }, [series]);

  const empty =
    children.length === 0
      ? "This scope has no children yet."
      : maxTotal === 0
        ? "No remaining weight in this scope."
        : null;

  if (empty)
    return (
      <AnalyticChartPanel
        title="Flowband"
        hint={
          <>
            Share of remaining weight per <em>{scope.nextLevelName}</em> over
            time.
          </>
        }
        scopeCaption={caption}
        legend={legend}
      >
        <div className="analyticEmpty">{empty}</div>
      </AnalyticChartPanel>
    );

  const seriesColorMap: Record<string, string> = {};
  children.forEach((child) => {
    if (child.ownColor && /^#[0-9a-fA-F]{6}$/.test(child.ownColor)) {
      seriesColorMap[child.id] = child.ownColor;
    }
  });

  const chartSpec: ChartSpec = {
    version: 1,
    id: "flowband-chart",
    workspaceId: "views",
    title: "Flowband",
    dataset: "work",
    type: "area",
    measure: { field: "count", agg: "sum" },
    range: { kind: "all" },
    filters: [],
    style: {
      palette: Object.keys(seriesColorMap).length > 0 ? "custom" : "theme",
      seriesColor: Object.keys(seriesColorMap).length > 0 ? seriesColorMap : undefined,
      legend: "off",
      dataLabels: false,
      gridlines: false,
      curve: "smooth",
    },
    createdBy: "system",
    createdAt: new Date().toISOString(),
    updatedBy: "system",
    updatedAt: new Date().toISOString(),
  };

  const chartData: ChartResult = {
    asOf: new Date().toISOString(),
    series: children.map((child, childIdx) => ({
      key: child.id,
      label: child.title,
      points: Array.from({ length: TICKS }, (_, tick) => ({
        x: `W${tick + 1}`,
        y: series[childIdx]?.[tick] ?? 0,
      })),
    })),
  };

  const lastTotal = series.reduce((s, r) => s + (r[TICKS - 1] ?? 0), 0) || 1;
  const now = Math.max(viewStartMs, Math.min(viewEndMs, currentMs));

  return (
    <AnalyticChartPanel
      title="Flowband"
      hint={
        <>
          Share of remaining weight per <em>{scope.nextLevelName}</em> over
          time.
        </>
      }
      scopeCaption={caption}
      legend={legend}
    >
      <div ref={parentRef} style={{ width: "100%" }}>
        <AreaChart
          spec={chartSpec}
          data={chartData}
          width={width}
          height={height}
          stack="percent"
          testId="flowband-areachart"
          onSeriesClick={
            scope.nextLevelName !== "checklist"
              ? (childId) => scope.drillInto(childId)
              : undefined
          }
          renderOverlay={({ xScale, innerWidth, innerHeight }) => {
            const firstTickX = xScale('W1') ?? 0;
            const lastTickX = xScale(`W${TICKS}`) ?? innerWidth;
            const nowRatio =
              viewEndMs === viewStartMs
                ? 0
                : Math.max(0, Math.min(1, (now - viewStartMs) / (viewEndMs - viewStartMs)));
            const nowX = firstTickX + nowRatio * (lastTickX - firstTickX);
            const step = width < 480 ? 4 : 2;

            return (
              <g className="flowband-overlay">
                <line
                  className="now"
                  x1={nowX}
                  x2={nowX}
                  y1={0}
                  y2={innerHeight}
                  style={{ stroke: 'var(--accent)' }}
                  strokeDasharray="4 3"
                  strokeWidth="1.5"
                />
                {Array.from({ length: TICKS }, (_, i) => i)
                  .filter((i) => i % step === 0)
                  .map((i) => (
                    <text
                      key={i}
                      x={xScale(`W${i + 1}`) ?? 0}
                      y={innerHeight + 16}
                      textAnchor="middle"
                      fontSize="10"
                      style={{ fill: 'var(--text-muted)' }}
                    >
                      W{i + 1}
                    </text>
                  ))}
              </g>
            );
          }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-[var(--text-muted)]">
        {children.map((c, i) => {
          const share = (series[i][TICKS - 1] ?? 0) / lastTotal;
          if (share <= 0.08) return null;
          return (
            <span key={c.id}>
              {c.title} · {Math.round(share * 100)}%
            </span>
          );
        })}
      </div>
    </AnalyticChartPanel>
  );
};
